import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App as CapApp } from '@capacitor/app';
import BiometricSecure from '../plugins/biometricSecure';
import API_BASE from '../config';

/**
 * Solvit Hub SSO is the only sign-in path. Username/password against
 * users.json is retired (backend /api/login returns 410 unless
 * ENABLE_PASSWORD_LOGIN is explicitly set for tests/local recovery).
 *
 * Native (iOS + Android): open Hub in an in-app browser
 * (@capacitor/browser → SFSafariViewController / Chrome Custom Tab) and
 * return via truva://auth-callback (see /auth/sso/callback's
 * platform=native|ios_native branch). Navigating the WebView itself to
 * Hub strands the user outside the Capacitor shell.
 *
 * Web: window.location.replace to Hub SSO.
 *
 * Important: Hub's redirect_uri is per-client and the sso_state cookie
 * must be set on the same host+port as that redirect_uri, or Hub rejects
 * the callback. In production the redirect_uri is the prod callback
 * (https://truva.solvit-ai.com/auth/sso/callback) and login is served from
 * that same origin, so a relative /auth/sso/login is correct. In local dev
 * the redirect_uri is http://localhost:5001/auth/sso/callback, so the login
 * URL must point at the local backend (port 5001) — never the prod URL —
 * otherwise the cookie lands on the wrong host and the callback fails with
 * {"error":"invalid state or missing code"}.
 */
function safePlatform() {
  try {
    return Capacitor.getPlatform() || 'web';
  } catch (_) {
    return 'web';
  }
}

function isNativePlatform() {
  try {
    if (typeof Capacitor.isNativePlatform === 'function') {
      return !!Capacitor.isNativePlatform();
    }
  } catch (_) { /* fall through */ }
  const p = safePlatform();
  return p === 'ios' || p === 'android';
}

function isLocalDevHost() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname || '';
  return host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
}

/** Absolute SSO login URL that matches Hub redirect_uri host. */
function resolveSsoLoginHref() {
  const base = String(API_BASE || '').replace(/\/+$/, '');
  if (base) return `${base}/auth/sso/login`;
  if (isLocalDevHost()) {
    // Backend on 5001 is plain HTTP and Hub's testclient redirect_uri is
    // http://localhost:5001/auth/sso/callback (exact match is required), so
    // the login URL must be http:// regardless of the frontend's own scheme
    // (this dev server can be served over https). Never derive scheme from
    // window.location.protocol here or Hub rejects the callback.
    return `http://${window.location.hostname}:5001/auth/sso/login`;
  }
  return '/auth/sso/login';
}

export default function Login() {
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [opening, setOpening] = useState(false);
  // One-shot: read + consume truva_just_signed_out once on mount, so both
  // the auto-redirect-skip effect below and the Sign-in button's own href
  // (prompt=login) can see it, instead of the effect alone consuming it.
  const [justSignedOut] = useState(() => {
    try {
      if (sessionStorage.getItem('truva_just_signed_out') === '1') {
        sessionStorage.removeItem('truva_just_signed_out');
        return true;
      }
    } catch (_) { /* ignore */ }
    return false;
  });
  const ssoHref = resolveSsoLoginHref();
  // prompt=login asks Hub to force reauthentication instead of silently
  // reusing its own separate hub_session cookie. Confirmed implemented on
  // Hub's side (routes/oidc.py oauth_authorize + login_page both honor it).
  // Sent after an explicit sign-out (so "sign out" then "sign in" isn't a
  // single silent click), and after an account_removed SSO error — without
  // it, retrying "Sign in with Solvit" on a deleted-from-Truva account just
  // silently reuses Hub's still-live session for that same account and
  // loops back to the same error, with no way to type different
  // credentials. A first-time visit still gets normal silent-SSO
  // convenience.
  const forceReauth = justSignedOut || errorCode === 'account_removed';
  const signInHref = forceReauth
    ? `${ssoHref}${ssoHref.includes('?') ? '&' : '?'}prompt=login`
    : ssoHref;
  const native = isNativePlatform();
  // Prefer platform=native for both; ios_native kept as alias on the backend.
  const nativePlatformParam = safePlatform() === 'ios' ? 'ios_native' : 'native';

  // Web: /auth/sso/callback redirects failures back to '/' with these
  // query params instead of leaving the browser on a raw JSON error page
  // (see _sso_error_redirect in backend/app.py). Read once, synchronously,
  // so the auto-bounce effect below can also see it and skip — otherwise
  // it would fire first and redirect straight back to Hub before the user
  // ever sees the error.
  const [ssoErrorMessage] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('sso_error_message') || '';
  });
  const [ssoErrorCode] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('sso_error') || '';
  });

  useEffect(() => {
    if (native || typeof window === 'undefined' || !ssoErrorMessage) return;
    setError(ssoErrorMessage);
    setErrorCode(ssoErrorCode);
    const params = new URLSearchParams(window.location.search);
    params.delete('sso_error');
    params.delete('sso_error_message');
    const rest = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
  }, [native, ssoErrorMessage, ssoErrorCode]);

  useEffect(() => {
    // Web only: send unauthenticated visitors straight to Hub.
    // Skip Capacitor WebView origins (handled by the Sign-in button +
    // Browser.open). Local CRA still offers a button so we don't surprise
    // bounce the tab to prod mid-debug — but the href is absolute prod.
    if (native) return;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const isCapacitorOrigin =
      origin.startsWith('capacitor://') ||
      origin.startsWith('http://localhost') ||
      origin.startsWith('https://localhost');
    if (isCapacitorOrigin) return;
    // Skip the auto-bounce right after an explicit sign-out — Hub's SSO
    // cookie is a separate session Truva's own logout can't touch, so
    // auto-redirecting here would silently re-authenticate off that
    // still-live cookie with no visible sign that sign-out did anything
    // at all. Show the real screen instead, with the prompt=login button.
    if (justSignedOut) return;
    // Skip the auto-bounce when we just came back from a failed SSO
    // attempt — same reasoning: show the error instead of silently
    // retrying Hub and looping the user right back into the same failure.
    if (ssoErrorMessage) return;
    window.location.replace(ssoHref);
  }, [ssoHref, native, justSignedOut, ssoErrorMessage]);

  useEffect(() => {
    if (!native) return;
    let handle = null;
    let cancelled = false;

    CapApp.addListener('appUrlOpen', ({ url }) => {
      if (!url || !String(url).startsWith('truva://auth-callback')) return;
      Browser.close().catch(() => {});
      try {
        const parsed = new URL(url);
        const token = parsed.searchParams.get('token');
        const userJson = parsed.searchParams.get('user');
        if (token && userJson) {
          localStorage.setItem('authToken', token);
          localStorage.setItem('user', userJson);
          localStorage.setItem('truva_session', token);
          // Persist a biometric-gated copy for the next cold-launch unlock.
          // Fire-and-forget: plaintext remains the always-on token; if this
          // is declined/unavailable the secure copy simply won't be written
          // and the app still signs in from localStorage.
          BiometricSecure.saveToken({ token }).catch(() => {});
          // Only reload for success — that's what picks up the new auth
          // state from localStorage. Reloading on an error path would wipe
          // the setError() below before it ever paints.
          window.location.reload();
          return;
        }
        const errorMessage = parsed.searchParams.get('error_message');
        setError(errorMessage || 'Sign-in did not complete. Please try again.');
        setErrorCode(parsed.searchParams.get('error') || '');
      } catch (e) {
        console.warn('[Login] failed to parse SSO callback url', e);
        setError('Sign-in did not complete. Please try again.');
      }
    }).then((h) => {
      if (cancelled) {
        h.remove().catch(() => {});
      } else {
        handle = h;
      }
    }).catch((e) => {
      console.warn('[Login] appUrlOpen listener failed', e);
    });

    return () => {
      cancelled = true;
      if (handle) handle.remove().catch(() => {});
    };
  }, [native]);

  // Fallback for accounts that can't go through Hub SSO (e.g. app-store
  // reviewer/demo logins with no real inbox to receive Hub's own signup
  // flow). Only usable when the backend has ENABLE_PASSWORD_LOGIN set —
  // off by default in production, see backend/app.py's /api/login. Kept
  // behind a toggle so it doesn't clutter the primary SSO flow for real
  // customers.
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pwUsername, setPwUsername] = useState('');
  const [pwPassword, setPwPassword] = useState('');
  const [pwSubmitting, setPwSubmitting] = useState(false);

  const submitPasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setPwSubmitting(true);
    try {
      const base = String(API_BASE || '').replace(/\/+$/, '');
      const res = await fetch(`${base}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: pwUsername.trim(), password: pwPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Sign-in failed. Please try again.');
        return;
      }
      const user = {
        username: data.username,
        name: data.name,
        email: data.email,
        numbers: data.numbers || [],
        role: data.role,
        tenant: null,
        plan: 'greet',
        entitlements: {},
        limits: {},
      };
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('truva_session', data.token);
      BiometricSecure.saveToken({ token: data.token }).catch(() => {});
      window.location.reload();
    } catch (e2) {
      console.warn('[Login] password login failed', e2);
      setError('Sign-in failed. Please try again.');
    } finally {
      setPwSubmitting(false);
    }
  };

  const startNativeSso = async () => {
    setError('');
    setOpening(true);
    const url = `${signInHref}${signInHref.includes('?') ? '&' : '?'}platform=${encodeURIComponent(nativePlatformParam)}`;
    try {
      await Browser.open({ url });
    } catch (e) {
      console.warn('[Login] Browser.open failed, falling back to <a>', e);
      // Last resort: open Hub in the system browser via a real anchor.
      // Deep-link return still works if the truva:// intent filter is registered.
      try {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e2) {
        setError('Could not open sign-in. Please try again.');
      }
    } finally {
      setOpening(false);
    }
  };

  const secondaryLinkStyle = {
    display: 'block',
    width: '100%',
    marginTop: 12,
    textAlign: 'center',
    fontSize: '0.95rem',
    color: 'var(--truva-text-secondary)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: 'inherit',
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-header">
          <h1>Truva</h1>
          <p>Sign in with your Solvit account</p>
        </div>

        {error && <div className="login-error">{error}</div>}

        {/* The primary action here is the one thing the user actually came
            here to do: sign in. Its label flips for account_removed since
            signInHref carries prompt=login in that state — "Sign in with
            Solvit" would be misleading (implies the usual silent one-click
            flow) when it's actually about to force a real credential
            prompt. The Hub-dashboard option (for "I'm still this person,
            just want my other apps") is a secondary link below rather than
            a second full-width button, so there's one obvious next step. */}
        {native ? (
          <button
            type="button"
            className="login-btn"
            data-testid="truva-login-sso"
            onClick={startNativeSso}
            disabled={opening}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'center',
              boxSizing: 'border-box',
            }}
          >
            {opening ? 'Opening…' : errorCode === 'account_removed' ? 'Sign in with a different account' : 'Sign in with Solvit'}
          </button>
        ) : (
          <a
            href={signInHref}
            className="login-btn"
            data-testid="truva-login-sso"
            onClick={() => setError('')}
            style={{
              display: 'block',
              textAlign: 'center',
              textDecoration: 'none',
              boxSizing: 'border-box',
            }}
          >
            {errorCode === 'account_removed' ? 'Sign in with a different account' : 'Sign in with Solvit'}
          </a>
        )}

        {errorCode === 'account_removed' && (
          // Truva access was removed, but the Solvit Hub identity (and any
          // other product subscriptions under it, e.g. MIP) is untouched.
          native ? (
            <button
              type="button"
              onClick={() => Browser.open({ url: 'https://hub.solvit-ai.com/dashboard' })}
              style={secondaryLinkStyle}
            >
              This is still my account — manage it in Solvit Hub
            </button>
          ) : (
            <a href="https://hub.solvit-ai.com/dashboard" style={secondaryLinkStyle}>
              This is still my account — manage it in Solvit Hub
            </a>
          )
        )}

        {/* Visible fallback if Browser plugin is missing from the APK */}
        {native && error ? (
          <a
            href={`${signInHref}${signInHref.includes('?') ? '&' : '?'}platform=${encodeURIComponent(nativePlatformParam)}`}
            style={{
              display: 'block',
              marginTop: 12,
              textAlign: 'center',
              fontSize: '0.9rem',
              color: 'var(--truva-action-blue)',
            }}
          >
            Open sign-in in browser
          </a>
        ) : null}

        {showPasswordForm ? (
          <form
            onSubmit={submitPasswordLogin}
            style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            <input
              type="text"
              placeholder="Username"
              value={pwUsername}
              onChange={(e) => setPwUsername(e.target.value)}
              autoComplete="username"
              style={{ padding: '10px 12px', borderRadius: 6, border: '1px solid var(--truva-border)' }}
            />
            <input
              type="password"
              placeholder="Password"
              value={pwPassword}
              onChange={(e) => setPwPassword(e.target.value)}
              autoComplete="current-password"
              style={{ padding: '10px 12px', borderRadius: 6, border: '1px solid var(--truva-border)' }}
            />
            <button
              type="submit"
              className="login-btn"
              disabled={pwSubmitting || !pwUsername || !pwPassword}
              style={{ display: 'block', width: '100%', textAlign: 'center', boxSizing: 'border-box' }}
            >
              {pwSubmitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => { setShowPasswordForm(true); setError(''); }}
            style={{
              display: 'block',
              width: '100%',
              marginTop: 16,
              textAlign: 'center',
              fontSize: '0.95rem',
              color: 'var(--truva-text-secondary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Use a password instead
          </button>
        )}
      </div>
    </div>
  );
}
